import React, { useState } from "react";
import PageHero from "../components/PageHero";
import { CheckCircle2, Upload, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { COMPANY, REQUIREMENT_CATEGORIES } from "../lib/constants";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import WhatsAppIcon from "../components/WhatsAppIcon";

const initial = {
  name: "",
  organization: "",
  designation: "",
  phone: "",
  whatsapp: "",
  email: "",
  requirement_category: "",
  requirement_details: "",
  quantity: "",
  delivery_location: "",
  expected_deadline: "",
  budget_range: "",
  message: "",
  consent: false,
};

function submitButtonContent(submitting, uploading) {
  if (submitting && uploading) return "Uploading file…";
  if (submitting) return "Submitting…";
  return (
    <>
      <CheckCircle2 className="w-4 h-4" /> Submit Requirement
    </>
  );
}

export default function SubmitRequirement() {
  const [form, setForm] = useState(initial);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please accept the consent checkbox");
      return;
    }
    if (!form.requirement_category) {
      toast.error("Please select a requirement category");
      return;
    }
    setSubmitting(true);
    try {
      let uploaded_url = null;
      if (file) {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded_url = up.data.url;
        setUploading(false);
      }
      const payload = { ...form, uploaded_file_url: uploaded_url };
      // remove empty email so EmailStr doesn't fail
      if (!payload.email) delete payload.email;
      const res = await api.post("/leads", payload);
      setDone(res.data);
      toast.success("Requirement submitted successfully");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  if (done) {
    const waLink = `https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(
      `Hello Melopa Invents, I just submitted a requirement (Ref: ${done.id.slice(0,8)}).`
    )}`;
    return (
      <div>
        <PageHero kicker="Thank You" title="We received your requirement" description="Our team will review the details and revert with a suitable proposal." />
        <section className="section">
          <div className="container-x max-w-2xl">
            <div className="card-premium p-6 text-center" data-testid="submit-success">
              <CheckCircle2 className="w-12 h-12 text-[var(--melopa-green)] mx-auto" />
              <h3 className="text-2xl font-bold text-navy mt-3">Requirement received</h3>
              <p className="text-[var(--melopa-muted)] mt-2">
                Reference ID: <span className="font-mono text-navy">{done.id.slice(0, 8).toUpperCase()}</span>
              </p>
              <div className="flex flex-wrap gap-2 justify-center mt-5">
                <a href={waLink} target="_blank" rel="noreferrer" className="btn-green">
                  <WhatsAppIcon className="w-4 h-4" /> Follow up on WhatsApp
                </a>
                <Link to="/" className="btn-outline">Back to Home</Link>
                <button className="btn-primary" onClick={() => { setDone(null); setForm(initial); setFile(null); }}>
                  Submit Another <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <PageHero
        kicker="Submit Requirement"
        title="Tell us about your requirement"
        description="For tenders, bulk supply, institutional procurement or infrastructure projects — share details and our team will respond promptly."
      />
      <section className="section">
        <div className="container-x grid lg:grid-cols-3 gap-8">
          <form onSubmit={submit} className="card-premium p-6 lg:col-span-2" data-testid="requirement-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Full Name *</label>
                <input className="input" required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="req-form-name" />
              </div>
              <div>
                <label className="label">Department / Institution / Company</label>
                <input className="input" value={form.organization} onChange={(e) => update("organization", e.target.value)} data-testid="req-form-org" />
              </div>
              <div>
                <label className="label">Designation</label>
                <input className="input" value={form.designation} onChange={(e) => update("designation", e.target.value)} />
              </div>
              <div>
                <label className="label">Phone Number *</label>
                <input className="input" required value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="req-form-phone" />
              </div>
              <div>
                <label className="label">WhatsApp Number</label>
                <input className="input" value={form.whatsapp} onChange={(e) => update("whatsapp", e.target.value)} />
              </div>
              <div>
                <label className="label">Email</label>
                <input className="input" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} />
              </div>
              <div>
                <label className="label">Requirement Category *</label>
                <select className="input" required value={form.requirement_category} onChange={(e) => update("requirement_category", e.target.value)} data-testid="req-form-category">
                  <option value="">Select category…</option>
                  {REQUIREMENT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Quantity</label>
                <input className="input" value={form.quantity} onChange={(e) => update("quantity", e.target.value)} placeholder="e.g. 50 units, 1 lot…" />
              </div>
              <div>
                <label className="label">Delivery Location</label>
                <input className="input" value={form.delivery_location} onChange={(e) => update("delivery_location", e.target.value)} placeholder="City / District" />
              </div>
              <div>
                <label className="label">Expected Deadline</label>
                <input className="input" type="date" value={form.expected_deadline} onChange={(e) => update("expected_deadline", e.target.value)} />
              </div>
              <div>
                <label className="label">Budget Range (optional)</label>
                <input className="input" value={form.budget_range} onChange={(e) => update("budget_range", e.target.value)} placeholder="e.g. ₹5–10 lakh" />
              </div>
              <div className="sm:col-span-2">
                <label className="label">Requirement Details *</label>
                <textarea className="input" required rows={4} value={form.requirement_details} onChange={(e) => update("requirement_details", e.target.value)} placeholder="Describe your requirement, specifications, scope of work, etc." data-testid="req-form-details" />
              </div>
              <div className="sm:col-span-2">
                <label className="label">Additional Message</label>
                <textarea className="input" rows={2} value={form.message} onChange={(e) => update("message", e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <label className="label">Upload Requirement / Tender Document (PDF, DOC, image; up to 25MB)</label>
                <label className="input flex items-center gap-2 cursor-pointer">
                  <Upload className="w-4 h-4 text-[var(--melopa-muted)]" />
                  <span className="flex-1 truncate text-sm text-[var(--melopa-muted)]">{file?.name || "Choose a file…"}</span>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xls,.xlsx,.zip"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    data-testid="req-form-file"
                  />
                </label>
              </div>
              <div className="sm:col-span-2 flex items-start gap-2">
                <input id="consent" type="checkbox" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} data-testid="req-form-consent" />
                <label htmlFor="consent" className="text-xs text-[var(--melopa-muted)]">
                  I consent to Melopa Invents Pvt Ltd contacting me regarding this requirement and storing the details for procurement purposes.
                </label>
              </div>
            </div>
            <button type="submit" disabled={submitting} className="btn-primary mt-6" data-testid="req-form-submit">
              {submitButtonContent(submitting, uploading)}
            </button>
          </form>
          <aside className="card-premium p-6 h-fit">
            <div className="kicker mb-2">Why share details?</div>
            <h3 className="text-xl font-bold text-navy">Faster, accurate response</h3>
            <div className="divider-gold" />
            <ul className="list-tick text-sm">
              <li>Quantity & specifications help us source accurately.</li>
              <li>Delivery location and deadline help with logistics.</li>
              <li>Tender / RFP documents speed up the response.</li>
              <li>Your details remain confidential.</li>
            </ul>
          </aside>
        </div>
      </section>
    </div>
  );
}
